/**
 * GA4 event reporting for lead forms, sent through Tag Manager.
 *
 * Events are pushed as objects keyed on `event` so they can be matched by a
 * Custom Event trigger. Calling gtag() directly does not work here: GA4 is
 * loaded by Tag Manager, so no page level config claims the command and the
 * hit is never sent.
 */

/**
 * Build the parameters sent with every lead form event.
 *
 * Page location, title and campaign attribution are collected automatically by
 * GA4, so they are deliberately not duplicated here.
 *
 * @param formDiv The HTML Element that contains the form container that contains the data-leadform-id element.
 */
const getBaseParams = (formDiv : Element) => ({
  'form_id':       formDiv.getAttribute('data-leadform-id') ?? '',
  'form_location': window.location.pathname,
});

/**
 * Push an event to the data layer, reporting whether it was available to
 * receive it.
 */
const sendEvent = (name : string, params : Record<string, unknown>) => {
  if(typeof dataLayer === 'undefined') {
    return false;
  }

  dataLayer.push({
    'event': name,
    ...params,
  });

  return true;
}

/**
 * Track a successful lead submission.
 *
 * @param formDiv The HTML Element that contains the form container that contains the data-leadform-id element.
 */
export const trackLeadSubmission = (formDiv : Element) => {
  sendEvent('generate_lead', getBaseParams(formDiv));
}

/**
 * Track a successful lead submission, then run the callback once the event has
 * been sent. Used when the page navigates away immediately after submitting,
 * which would otherwise cancel the request before it leaves the browser.
 *
 * The callback always runs, even when the data layer is missing or Tag Manager
 * never calls back, so analytics can never block the redirect.
 *
 * @param formDiv The HTML Element that contains the form container that contains the data-leadform-id element.
 * @param callback Runs once the event is sent, or once the timeout elapses.
 */
export const trackLeadSubmissionThen = (formDiv : Element, callback : () => void) => {
  const timeout = 1000;
  let completed = false;

  const runOnce = () => {
    if(completed) {
      return;
    }

    completed = true;
    callback();
  }

  // Tag Manager runs eventCallback once every tag for this event has fired.
  const sent = sendEvent('generate_lead', {
    ...getBaseParams(formDiv),
    'eventCallback': runOnce,
    'eventTimeout':  timeout,
  });

  if(!sent) {
    runOnce();
    return;
  }

  // Guards against Tag Manager never invoking the callback.
  window.setTimeout(runOnce, timeout);
}

/**
 * Track a failed lead submission.
 *
 * @param formDiv The HTML Element that contains the form container that contains the data-leadform-id element.
 * @param errorType One of validation, server or network.
 * @param errorCount The number of fields that failed validation, where applicable.
 */
export const trackLeadError = (formDiv : Element, errorType : string, errorCount = 0) => {
  sendEvent('lead_form_error', {
    ...getBaseParams(formDiv),
    'error_type':  errorType,
    'error_count': errorCount,
  });
}
